import React, {
  useEffect,
  useState,
  lazy,
  Suspense,
  useCallback,
  useMemo
} from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import Cookies from "js-cookie";

// --- Configuration & Services ---
import BankConfigFactory from "External-API-modules/Services/configExporter.ts";
import AuthService from "External-API-modules/Services/AuthService.ts";
import { requestFactory } from "./bankProvider/interceptors/requestFactory";
import { EventNames } from "./constants/eventNames";
import { BANK_NAME } from "./constants/enum";
import { sendMessageToApp } from "utils/functions";

// --- Context ---
import { useTransaction } from "./context/TransactionContext";
import { useBankContext } from "./context/BankContext";
import { useBridgeData } from "./context/BridgeDataContext";

// --- Components & Containers (Lazy Loaded) ---
const CardManagement = lazy(() => import("containers/CardManagement"));
const ConvertToEMI = lazy(() => import("containers/ConvertToEMI"));
const EMIConversionResponse = lazy(
  () => import("containers/EMIConversionResponse")
);
const ManageTransaction = lazy(() => import("containers/ManageTransaction/"));
const Deposit = lazy(() => import("containers/Deposit/"));
const PaymentAnimations = lazy(
  () => import("containers/PaymentStateAnimationPages")
);
const SuperCardDetails = lazy(() => import("containers/SuperCardDetails"));
const PaymentSuccess = lazy(() => import("containers/PaymentSuccess"));
const IncreaseLimit = lazy(() => import("containers/IncreaseLimit"));
const DepositDetails = lazy(() => import("containers/CardDepositDetails"));
const DepositWithdrawPayment = lazy(
  () => import("containers/CardDepositDetails/withdrawPaymentPage")
);
const CardTransaction = lazy(() => import("containers/CardTransaction"));
const TermsOfService = lazy(() => import("containers/TermsOfService"));
const ErrorScreen = lazy(() => import("containers/ErrorScreens"));
const CardClosed = lazy(() => import("containers/CardClosed"));
const CardPin = lazy(() => import("containers/CardPIN/cardPin"));
const LoginModal = lazy(() => import("components/LoginModal"));
const SkeletonPageLoader = lazy(() => import("components/SkeletonPageLoader/index.jsx"));

// --- Protected Routes ---
import { Protected, ProtectedCardInfo } from "./protected";

// --- Styles ---
import "styles/global.scss";

// --- Utils ---
import { 
  updateBankBridgeData, 
  getBankBridgeData, 
  pollForBridgeData,
  hasBridgeData,
  getAllBankBridgeData
} from "./utils/localStorageManage";
import { setupGlobalErrorHandlers, logError } from "utils/errorLogger.ts";
import { getBankedItem, setBankedItem, removeBankedItem } from "./utils/bankedLocalStorage";

// --- Utils ---
import { initTracking, trackEvent } from "./tracking/trackWrapper";


// --- Constants & Config ---
const BASE_PATH = process.env.PUBLIC_URL;

// --- Environment Logging ---
console.log("--- Application Configuration ---");
console.log("UI Server: UAT");
console.log("PUBLIC_URL:", process.env.PUBLIC_URL);
console.log("ENVIRONMENT:", process.env.REACT_APP_ENVIRONMENT);
console.log("PROXY_SERVER_URL:", process.env.REACT_APP_PROXY_SERVER_URL);
console.log("PCI_UI_SERVER_URL:", process.env.REACT_APP_PCI_UI_SERVER_URL);

// --- Main App Component ---
const App = () => {

  const [previousUrl, setPreviousUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const { userDetails, setIsLogin, setUserDetails } = useTransaction();
  const { setBankConfig } = useBankContext();
  const { 
    processBridgeData, 
    getActiveBankData, 
    isInitialized,
    markInitialized,
    initializationError,
    activeBankKey
  } = useBridgeData();

  // Read cookies/localStorage and derive current shared and bridge context
  const computeDataFromStorage = useCallback(() => {
    let sharedData = {};
    let bridgeData = {};
    let rawBridgeData = null;
    let currentBankKey = "";

    try {
      sharedData = JSON.parse(Cookies.get("sharedData") || "{}");
    } catch (e) {
      console.error("Error parsing sharedData cookie:", e);
    }

    try {
      const legacyBridgeDataString = localStorage.getItem("bridgeData");
      const legacyBridgeData = legacyBridgeDataString ? JSON.parse(legacyBridgeDataString) : {};

      currentBankKey =  legacyBridgeData.bank?.toLowerCase();

      // Store raw data for processing later (don't process inside this function)
      if (Object.keys(legacyBridgeData).length > 0 && currentBankKey) {
        console.log(`[App] Found bridgeData for bank: ${currentBankKey}`);
        updateBankBridgeData(legacyBridgeData);
        rawBridgeData = legacyBridgeData;
      }

      if (currentBankKey) {
        bridgeData = getBankBridgeData(currentBankKey) || {};
      }
    } catch (e) {
      console.error("Error processing bridgeData (new or legacy):", e);
        logError(e, {
          context: "CAUGHT_ERROR",
          message: "Error processing bridgeData (new or legacy)",
          customAttributes: { currentBankKey }
        });
    }

    if (bridgeData.regId) {
      setBankedItem(currentBankKey, "regId", bridgeData.regId);
    }

    return { vals: sharedData, context: bridgeData, rawBridgeData };
  }, []); // No dependencies - pure data reading function

  // Initialize state from current storage snapshot
  const initialData = useMemo(() => computeDataFromStorage(), [computeDataFromStorage]);
  const [vals, setVals] = useState({});
  const [context, setContext] = useState({});
  const [hasInitialized, setHasInitialized] = useState(false);
  // ...

  window.contextDetails = context;
  
  //Generates a new access token using the refresh token for Kotak.
  const handleGenerateToken = useCallback(
    async (refreshToken) => {
      if (!context.bank) return;
      try {
        const tokenRequestBody = await requestFactory(
          context.bank,
          "token",
          context,
          { refresh_token: refreshToken, grant_type: "refresh_token" }
        );

        const service = await AuthService.getService(context.bank);
        const fetchTokenResponse = await service.fetchAuthToken(
          tokenRequestBody
        );

        if (fetchTokenResponse?.isLoginSuccessful) {
          setIsLogin(true);
          setLoading(false);
          trackEvent(EventNames.TRACK_TOKEN_REGENERATION_SUCCESS, {
            bank: context.bank
          });
          console.log("Access token regenerated successfully.");
        }
      } catch (error) {
        console.error("Token generation failed:", error);
          logError(error, {
            context: "ACTION_ERROR",
            message: "Token generation failed",
            customAttributes: { bank: context.bank }
          });
      }
    },
    [context, setIsLogin]
  );

  //Checks for token and initiates refresh if required for Kotak, or validates Axis session.
  const checkAndRefreshToken = useCallback(async () => {
    const accessToken = Cookies.get("access_token");
    const refreshToken = Cookies.get("refresh_token");
    const validationCookie = Cookies.get("validation");

    const isKotak = context.bank === BANK_NAME.KOTAK;
    const isAxis = context.bank === BANK_NAME.AXIS;
    const hasRefreshToken = refreshToken && refreshToken !== "undefined";

    if (isKotak && !accessToken && hasRefreshToken) {
      trackEvent(EventNames.TRACK_SESSION_MANAGEMENT, {
        bank: context.bank,
        stage: 'kotak_refresh_token_flow',
        hasAccessToken: false,
        hasRefreshToken: true
      });
      await handleGenerateToken(refreshToken);
    } else if (isKotak && accessToken) {
      trackEvent(EventNames.TRACK_SESSION_MANAGEMENT, {
        bank: context.bank,
        stage: 'kotak_access_token_present'
      });
      setIsLogin(true);
      setLoading(false);
    } else if (isAxis && validationCookie) {
      // Axis: Check if validation token is still valid (5 minute expiry)
      try {
        const validationData = JSON.parse(validationCookie);
        const isExpired = new Date().getTime() - validationData.time >= 5 * 60 * 1000;
        if (isExpired) {
          trackEvent(EventNames.TRACK_SESSION_MANAGEMENT, {
            bank: context.bank,
            stage: 'axis_validation_expired'
          });
          Cookies.remove("validation");
          setIsLogin(false);
        } else {
          trackEvent(EventNames.TRACK_SESSION_MANAGEMENT, {
            bank: context.bank,
            stage: 'axis_validation_valid'
          });
          setIsLogin(true);
          setLoading(false);
        }
      } catch (e) {
        console.error("Error parsing validation cookie:", e);
        trackEvent(EventNames.TRACK_SESSION_MANAGEMENT, {
          bank: context.bank,
          stage: 'axis_validation_parse_error'
        });
        Cookies.remove("validation");
        setIsLogin(false);
      }
    } else if (isAxis && !validationCookie) {
      trackEvent(EventNames.TRACK_SESSION_MANAGEMENT, {
        bank: context.bank,
        stage: 'axis_validation_missing'
      });
      setIsLogin(false);
    }
  }, [context.bank, handleGenerateToken, setIsLogin]);


  // Sets user details and loads bank configuration from context or an empty default.
  const setUserDetailsFromContext = useCallback(() => {
    let newDetails = {};
    let bankKey = (context.bank || process.env.BANK)?.toLowerCase();
    if (Object.keys(context).length) {
      newDetails = {
        name: context.name,
        email: context.email,
        cardAccountNumber: context.cardAccountNumber,
        phoneNumber: context.phoneNumber,
        cardSerNo: context.cardSerNo,
        deviceId: context.deviceId,
        superMoneyAccountId: context.superMoneyAccountId,
        last4Digits: context.last4Digits,
        bank: bankKey,
        appRefNumber: context?.appRefNumber,
        crnNumber: context?.crnNumber,
        codeVerifier: context?.codeVerifier
      };
    }

    if (bankKey && Object.keys(newDetails).length > 0) {
        updateBankBridgeData({ ...newDetails, bank: bankKey });
    }
    setUserDetails(newDetails);

    if (bankKey) {
      BankConfigFactory.getBankConfig(bankKey)
        .then((config) => setBankConfig(config))
        .catch((error) =>
          console.error(`Failed to load bank config for ${bankKey}: ${error.message}`)
        );
    }
  }, [context, vals, setUserDetails, setBankConfig]); // Dependencies are stable now


  // Initializes analytics and handles unhandled promise rejections.
  const initAnalytics = useCallback(() => {
    const dispatchClientSideUnhandledEvent = (event) => {
    trackEvent(EventNames.CLIENT_SIDE_UNHANDLED_REJECTION, {
        reason: event.reason || "Not handled in Client side"
      });
    };

    trackEvent(EventNames.LCM_LOGIN_SUCCESS, {
      appName: "LCM"
    });

    window.addEventListener(
      "unhandledrejection",
      dispatchClientSideUnhandledEvent
    );

    return () => {
      window.removeEventListener(
        "unhandledrejection",
        dispatchClientSideUnhandledEvent
      );
    };
  }, [vals?.userDetails?.superMoneyAccountId]); // Dependency on the stable ID source

  useEffect(() => {
    initTracking();
  }, []);

  // --- Master Setup Effect (Run once on mount) ---
  useEffect(() => {

    // 1. Setup window unload handler and previous URL check
    const handleBeforeUnload = () => {
      sessionStorage.setItem("previousUrl", window.location.pathname);
      // Clear specific local/session storage items
      if (context?.bank) {
        removeBankedItem(context.bank, "payID");
        removeBankedItem(context.bank, "paymentSt");
        removeBankedItem(context.bank, "amt");
        removeBankedItem(context.bank, "accountId");
        removeBankedItem(context.bank, "codeVerifier");
      } else {
        localStorage.removeItem("payID");
        localStorage.removeItem("paymentSt");
        localStorage.removeItem("amt");
        localStorage.removeItem("accountId");
        localStorage.removeItem("codeVerifier");
      }
      sessionStorage.removeItem("transactionScrollPosition");
      
    };

    const storedUrl = sessionStorage.getItem("previousUrl");
    if (storedUrl && storedUrl !== window.location.pathname) {
      setPreviousUrl(storedUrl);
    }
    sessionStorage.removeItem("previousUrl"); // Clear after checking

    // 2. Setup event listeners, including the analytics initialization
    window.addEventListener("beforeunload", handleBeforeUnload);
    setupGlobalErrorHandlers();
    const cleanupAnalytics = initAnalytics(); 

    // The cleanup function
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      cleanupAnalytics && cleanupAnalytics(); 
    };
  }, [initAnalytics]);


  // Phase 1: Data Acquisition - Poll for bridge data with blocking UI
  useEffect(() => {
    let cancelled = false;

    const initializeBridgeData = async () => {
      console.log("[App] Starting bridge data initialization");

      try {
        // Get current storage state
        const { vals: sharedVals, context: bridgeContext, rawBridgeData } = computeDataFromStorage();
        
        console.log("[App] Storage check:", {
          hasUnifiedData: hasBridgeData(),
          hasRawData: !!rawBridgeData,
          hasSharedVals: !!sharedVals?.userDetails,
          hasContext: !!(bridgeContext && Object.keys(bridgeContext).length > 0)
        });

        // Check if we have any bridge data (cached or raw)
        if (hasBridgeData() || rawBridgeData) {
          console.log("[App] Bridge data found in cache");
          
          // Process raw bridge data if available (from legacy storage)
          if (rawBridgeData && rawBridgeData.bank) {
            console.log("[App] Processing legacy bridgeData for:", rawBridgeData.bank);
            processBridgeData(rawBridgeData, true);
          } else {
            // Process cached unified data
            const allData = getAllBankBridgeData();
            console.log("[App] Processing cached unified data, banks:", Object.keys(allData));
            if (Object.keys(allData).length > 0) {
              processBridgeData(allData, false);
            }
          }
          
          // Re-read storage after processing to ensure freshness
          const { vals: nextVals, context: nextContext } = computeDataFromStorage();
          if (nextVals?.userDetails) {
            console.log("[App] Updating vals from sharedData");
            setVals(nextVals);
          } else {
            setVals({});
          }
          if (nextContext && Object.keys(nextContext).length > 0) {
            console.log("[App] Updating context from active bank data, bank:", nextContext.bank);
            setContext(nextContext);
          } else {
            setContext({});
          }
          
          markInitialized(true);
          console.log("[App] Initialization complete (from cache)");
          return;
        }

        // If not found, start polling 
        // to move these in constats file
        console.log("[App] Bridge data not found, starting polling...");
        const pollingResult = await pollForBridgeData({
          maxAttempts: 40,
          intervalMs: 500,
          timeoutMs: 20000
        });

        if (cancelled) {
          console.log("[App] Initialization cancelled");
          return;
        }

        if (pollingResult.success && pollingResult.data) {
          console.log("[App] Bridge data acquired successfully via polling");
          
          // Process the polled data
          processBridgeData(pollingResult.data, true);
          
          // Re-read storage after processing
          const { vals: nextVals, context: nextContext } = computeDataFromStorage();
          if (nextVals?.userDetails) {
            setVals(nextVals);
          } else {
            setVals({});
          }
          if (nextContext && Object.keys(nextContext).length > 0) {
            setContext(nextContext);
          } else {
            setContext({});
          }
          
          markInitialized(true);
          console.log("[App] Initialization complete (from polling)");
        } else {
          console.error("[App] Failed to acquire bridge data:", pollingResult.error);
          markInitialized(false, pollingResult.error);
        }
      } catch (error) {
        console.error("[App] Bridge data initialization error:", error);
        if (!cancelled) {
          markInitialized(false, "An unexpected error occurred during initialization");
        }
        // Log the full error for debugging
        try {
          logError(error, {
            context: "INITIALIZATION_ERROR",
            message: "Failed to initialize bridge data"
          });
        } catch (_) {
          console.error("[App] Could not log error:", error);
        }
      }
    };

    initializeBridgeData();

    return () => {
      cancelled = true;
    };
  }, [computeDataFromStorage, processBridgeData, markInitialized]);

  // Phase 2: Data Processing - Once data exists, perform initialization exactly once
  useEffect(() => {
    if (hasInitialized) return;
    if (!isInitialized) return; // Wait for bridge data context to be initialized
    
    const hasContext = Boolean(vals?.userDetails) || (context && Object.keys(context).length > 0);
    if (!hasContext) return;

    setUserDetailsFromContext();
    checkAndRefreshToken();
    setHasInitialized(true);
  }, [vals, context, hasInitialized, isInitialized, setUserDetailsFromContext, checkAndRefreshToken]);

  // Handle Native bridge communication (nav items)
  useEffect(() => {
    const handleMessage = (message) => {
      try {
        const data = JSON.parse(message.data);
        if (data.type === "MENU_ITEMS") {
          const { leftBtn, rightBtn, headerTitle } = data.params;
          // Store nav items
          if (context?.bank) {
            setBankedItem(context.bank, "menuItems", JSON.stringify({ leftBtn, rightBtn, headerTitle }));
          } else {
            localStorage.setItem("menuItems", JSON.stringify({ leftBtn, rightBtn, headerTitle }));
          }
        }
      } catch (error) {
        console.error("Error parsing message from WebView:", error);
      }
    };

    if (window.ReactNativeWebView) {
      // Listen for response
      window.addEventListener("message", handleMessage);
    }

    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, []); // Run only on mount/unmount

  // Idle RT expiry monitor: if RT expired, close webview (Kotak)
  useEffect(() => {
    const isKotak = context.bank === BANK_NAME.KOTAK;
    if (!isKotak) return;

    const checkRtExpiryAndClose = () => {
      try {
        const expStr = sessionStorage.getItem("kotak_rt_exp");
        if (!expStr) return;
        const exp = Number(expStr);
        if (!Number.isNaN(exp) && Date.now() > exp) {
          sendMessageToApp({ type: "SM__CLOSE_WEBVIEW" });
        }
      } catch (e) {
        console.error("RT expiry check failed:", e);
      }
    };

    checkRtExpiryAndClose();
    const id = setInterval(checkRtExpiryAndClose, 15000);
    return () => clearInterval(id);
  }, [context.bank]);

  // Keep local context in sync with active bank data from BridgeDataContext
  useEffect(() => {
    if (!activeBankKey) return;
    try {
      const active = getActiveBankData?.();
      if (
        active &&
        Object.keys(active).length > 0 &&
        active.bank &&
        active.bank !== context?.bank
      ) {
        console.log("[App] Syncing context from activeBankKey change, bank:", active.bank);
        setContext(active);
      }
    } catch (e) {
      console.error("Failed to sync active bank context:", e);
    }
  }, [activeBankKey]);


  return (
    <BrowserRouter basename={BASE_PATH}>
      <Suspense fallback={<></>}>
        {/* Phase 1: Blocking Modal during initialization*/}
        {!isInitialized && (
          <SkeletonPageLoader/>
        )}
        <LoginModal />
        <Routes>
          {/* Base Path */}
          <Route path="/" />

          {/* Public Routes */}
          <Route path="/inc-limit" element={<IncreaseLimit />} />
          <Route path="/dep-details" element={<DepositDetails />} />
          <Route
            path="/dep-payment"
            element={<DepositWithdrawPayment />}
          />
          <Route path="/super-card" element={<CardManagement />} />
          <Route path="/card-transactions" element={<CardTransaction />} />
          <Route path="/payment-success" element={<PaymentSuccess />} />
          <Route path="/last-statement" element={<CardTransaction />} />
          <Route path="/terms-of-services" element={<TermsOfService />} />
          <Route path="/error" element={<ErrorScreen />} />
          <Route path="/card-closed" element={<CardClosed />} />
          <Route path="/payment-animation" element={<PaymentAnimations />} />
          <Route path="/change-card-pin" element={<CardPin />} />

          {/* Protected Routes (Requires Auth) */}
          <Route element={<Protected />}>
            <Route path="/convert-to-emi" element={<ConvertToEMI />} />
            <Route path="/emi-conversion" element={<EMIConversionResponse />} />
            <Route path="/manage-transaction" element={<ManageTransaction />} />
            <Route path="/deposit" element={<Deposit />} />
            <Route element={<ProtectedCardInfo />}>
              <Route path="/super-card-details" element={<SuperCardDetails />} />
            </Route>
          </Route>

          {/* Fallback/Previous URL Redirect */}
          {previousUrl && (
            <Route path="*" element={<Navigate to={previousUrl} replace />} />
          )}
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
};

export default App;

